import * as React from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { createStyles, makeStyles, Theme, Tooltip } from '@material-ui/core';
import { ReactComponent as LockIcon } from '../../assets/svg/icons/lock.svg';
import { ReactComponent as QueryBookmarkIcon } from '../../assets/svg/icons/bookmark-querytile.svg';
import { get, getConfig, getText } from '../../helpers';
import DataFlag from '../data-flag';

const useStyles = makeStyles((theme: Theme) => {
    return createStyles({
        tooltip: {
            backgroundColor: '#fff',
            color: 'black',
            maxWidth: theme.typography.pxToRem(450),
            fontSize: theme.typography.pxToRem(14),
            border: '1px solid #ccc',
            fontFamily: 'Akk Pro, Arial, sans-serif'
        }
    });
});

const signInUrl = getConfig('urls.mi.ssacs.oktaSignIn');
const Window = window as AnonymousObject;
Window.dataLayer = Window.dataLayer || [];

export default function QueryTile({ query, isLoggedIn, bookmarks, className = '', page }: {query: Query; isLoggedIn: boolean; bookmarks: Bookmarks; className?: string; page?: string}) {
    const [isTruncated, setIsTruncated] = React.useState<boolean>(false);
    const textRef = React.useRef(null);
    const location = useLocation();
    const langCode = getText('langCode');
    const signInToViewCode = getText('pages.querylibrary.signInToViewCode');
    const datasetFilters = useSelector((state: RootStoreState) => get(state, 'filters.datasets', []));
    // const toValue = query.queryId === 0 ? `${location.pathname}` : `${location.pathname}/query-(${query.queryId})`;

    const getUrl = () => {
        let url = window.location.pathname;
        const path = url.split('/');
        path.pop();
        url = path.join('/');
        if (query.queryId === 0) {
            const filters = {
                dataset: query.associatedDatasets && query.associatedDatasets.map((dataset1: AssociatedDataset) => {
                    // we are searching associated folter by id, and take en language name of filter
                    // it helps as to fix filters breaking for native languages (filters list is always in EN)
                    const dtsFilt = datasetFilters.find((el: {id: string}) => `dataset-${dataset1.DatasetKey}` === el.id);
                    if (dtsFilt) {
                        return dtsFilt.name;
                    }
                    return dataset1.DatasetName;
                })
            };
            const encodedFilters = encodeURIComponent(JSON.stringify(filters));
            return `${url}?filters=${encodedFilters}`;
        } if (location.pathname.indexOf('query-(') > 0) {
            return `${url}/query-(${query.queryId})`;
        }
        return `${location.pathname}/query-(${query.queryId})`;
    };

    let isBookmarked = false;
    if (isLoggedIn) {
        isBookmarked = bookmarks.queries && bookmarks.queries.some(x => x.keyId.toString() === query.queryId.toString());
    }

    const handleMouseEnter = () => {
        const el: HTMLElement = (textRef.current || {}) as HTMLElement;
        if (el && el.scrollHeight > el.clientHeight) {
            setIsTruncated(true);
        }
    };

    const onLoginClick = (event: React.SyntheticEvent) => {
        event.stopPropagation();
        let returnUrl = '';
        if (process.env.NODE_ENV === 'production') {
            if (window.location.pathname.length > 1) {
                returnUrl = `&mpurl=${encodeURIComponent(window.location.pathname)}`;
            }
            event.preventDefault();
            window.location.href = `${signInUrl + returnUrl}`;
        }
    };

    const handleClick = (event: React.SyntheticEvent) => {
        if (query.name === 'View all Queries for this Dataset(s) >') {
            event.preventDefault();
            window.open(getUrl(), '_blank', 'noopener,noreferrer');
            Window.dataLayer.push({
                event: 'MoreQueriesButton',
                buttonTitle: query.name
            });
        } else {
            Window.dataLayer.push({
                event: 'QueryLibraryTileClick',
                queryId: typeof query.queryId === 'string' ? Number(query.queryId) : query.queryId,
                queryName: query.name
            });
        }
    };

    return (
        <Tooltip classes={useStyles(0)} placement="top" arrow disableFocusListener disableTouchListener title={isTruncated ? query.name : ''}>
            <Link tabIndex={-1} className={`query-card ${query.name === 'dummy' ? 'dummy-block' : ''} ${query.queryId === 0 ? 'view-all' : ''} ${className} ${window.matchMedia('(pointer:coarse)').matches && page === 'profile' ? 'query-mobile-view' : ''}`} onMouseEnter={handleMouseEnter} onClick={handleClick} onKeyDown={e => e.key === 'Enter' && handleClick(e)} to={getUrl}>
                <div style={{ display: 'flex' }}>
                    <DataFlag datasetAddedDate={query.versiondate || ''} isSmall={false} querySrc="tile" />
                    <div className="query-card__bookmark">
                        {isLoggedIn && isBookmarked && <QueryBookmarkIcon className="data-icon__tile-bookmark" />}
                    </div>
                </div>
                <h3 className="query-card__datasets">{query.datasets.join(', ')}</h3>
                <p ref={textRef} className="query-card__name">{query.name}</p>
                {!isLoggedIn && (
                    <div className="query-card__auth-block">
                        <a
                            href={`${signInUrl}&mpurl=${encodeURIComponent(window.location.pathname)}`}
                            target="_self"
                            rel="noopener noreferrer"
                            className="query-library-tile--login-link"
                            onClick={onLoginClick}
                            onKeyDown={e => e.key === 'Enter' && onLoginClick(e)}
                            data-testid="query-library-tile--login-link"
                        >
                            <span>{signInToViewCode}</span>
                            <LockIcon />
                        </a>
                    </div>
                )}
            </Link>
        </Tooltip>);
}
